import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";

// APIs
import { GetDepartments } from "../../../api/masters/departmentAPI";
import { GetCaseById, UpdateCase } from "../../../api/masters/caseAPI";
import useKeyboardSubmit from "../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../hooks/useEnterNavigation";

// ✅ Validation Schema (aligned with your Mongoose model)
const schema = yup.object().shape({
  name: yup.string().required("Case name is required"),
  departmentId: yup.string().required("Department is required"),
  ob: yup.string().oneOf(["Yes", "No"]).required()
});

const EditCase = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [departments, setDepartments] = useState([]);

  const { id } = useParams();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema)
  });

  // Fetch dropdown data
  const fetchDepartments = async () => {
    try {
      const res = await GetDepartments();
      if (res.success) setDepartments(res.data);
      else showToast("error", res.message);
    } catch (err) {
      showToast("error", err.message);
    }
  };

  const fetchData = async () => {
    try {
      const response = await GetCaseById(id); // Make sure login function returns token

      if (response.success) {
        setValue("name", response.data.name); // Set default values
        setValue("departmentId", response.data.departmentId);
        setValue("ob", response.data.ob);
      } else {
        // setError(response.message);
        showToast("error", response.message);
      }
    } catch (error) {
      // setError(error); // Handle login errors
      showToast("error", error);
    }
  };

  useEffect(
    () => {
      const loadData = async () => {
        try {
          dispatch({ type: "LOADER_START" });

          await fetchDepartments();
          await fetchData();
        } catch (error) {
          showToast("error", error);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      };

      loadData();
    },
    [id]
  );

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });
      const res = await UpdateCase(id, data);
      if (res.success) {
        showToast("success", res.message);
      } else {
        showToast("error", res.message);
      }
    } catch (err) {
      showToast("error", err.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
  useKeyboardSubmit(handleSubmit, onSubmit);
  useEnterNavigation(formRef);

  return (
    <Fragment>
      <Breadcrumb parent="Case" title="Edit Case" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header d-flex justify-content-between">
                <h5>Edit Case</h5>
                <Link to={`/masters/case/list`}>
                  <button className="btn btn-primary" id="list">
                    List Case
                  </button>
                  <UncontrolledTooltip placement="top" target="list">
                    List Case
                  </UncontrolledTooltip>
                </Link>
              </div>

              <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label>Name *</label>
                      <input
                        className="form-control"
                        placeholder="Case Name"
                        {...register("name")}
                      />
                      {errors.name &&
                        <p className="text-danger">
                          {errors.name.message}
                        </p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>Department *</label>
                      <select
                        className="form-control"
                        {...register("departmentId")}
                      >
                        <option value="">Select Department</option>
                        {departments.map(dep =>
                          <option key={dep._id} value={dep._id}>
                            {dep.name}
                          </option>
                        )}
                      </select>
                      {errors.departmentId &&
                        <p className="text-danger">
                          {errors.departmentId.message}
                        </p>}
                    </div>

                    <div className="col-md-6 mb-3">
                      <label>OB *</label>
                      <select className="form-control" {...register("ob")}>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default EditCase;
